/**
 * SetupTwoFactorPage — /setup-2fa?token=<jwt>
 *
 * Opened from the registration welcome email link.
 * Lets the user scan the Google Authenticator QR code and enable 2FA
 * without being fully logged in.
 */
import { useState, useEffect, useRef } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { Loader, Check, Copy, ShieldCheck, AlertCircle, Eye, EyeOff } from 'lucide-react'
import useAuthStore from '../store/auth-store'

// ─── 6-box OTP Input ──────────────────────────────────────────────────────────
function OtpInput({ value, onChange, disabled }) {
  const refs = Array.from({ length: 6 }, () => useRef(null))

  const handleKey = (e, idx) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const digits = value.split('')
      digits[idx] = ''
      onChange(digits.join(''))
      if (idx > 0) refs[idx - 1].current?.focus()
    }
  }

  const handleChange = (e, idx) => {
    const ch = e.target.value.replace(/\D/g, '').slice(-1)
    const digits = value.padEnd(6, ' ').split('')
    digits[idx] = ch
    const next = digits.join('').replace(/ /g, '')
    onChange(next)
    if (ch && idx < 5) refs[idx + 1].current?.focus()
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    onChange(pasted)
    refs[Math.min(pasted.length, 5)].current?.focus()
  }

  return (
    <div className="flex items-center justify-center gap-2">
      {[0, 1, 2].map(i => (
        <input
          key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={1}
          disabled={disabled} value={value[i] || ''}
          onChange={e => handleChange(e, i)} onKeyDown={e => handleKey(e, i)} onPaste={handlePaste}
          className="w-12 h-13 text-center text-xl font-bold border-2 border-gray-300 rounded-lg
                     focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 transition py-2.5"
        />
      ))}
      <span className="text-2xl font-light text-gray-400 mx-1">-</span>
      {[3, 4, 5].map(i => (
        <input
          key={i} ref={refs[i]} type="text" inputMode="numeric" maxLength={1}
          disabled={disabled} value={value[i] || ''}
          onChange={e => handleChange(e, i)} onKeyDown={e => handleKey(e, i)} onPaste={handlePaste}
          className="w-12 h-13 text-center text-xl font-bold border-2 border-gray-300 rounded-lg
                     focus:outline-none focus:border-[#358ECD] disabled:bg-gray-100 transition py-2.5"
        />
      ))}
    </div>
  )
}

export default function SetupTwoFactorPage() {
  const [searchParams]       = useSearchParams()
  const navigate             = useNavigate()
  const { twoFactorApi }     = useAuthStore()
  const setupToken           = searchParams.get('token') || ''

  const [loadState,    setLoadState]    = useState('loading')  // loading | ready | error | done
  const [qrCode,       setQrCode]       = useState('')
  const [secret,       setSecret]       = useState('')
  const [backupCodes,  setBackupCodes]  = useState([])
  const [showSecret,   setShowSecret]   = useState(false)
  const [otpValue,     setOtpValue]     = useState('')
  const [submitting,   setSubmitting]   = useState(false)
  const [submitError,  setSubmitError]  = useState('')
  const [copied,       setCopied]       = useState(false)

  // ── Fetch QR code on mount ────────────────────────────────────────────────
  useEffect(() => {
    if (!setupToken) { setLoadState('error'); return }
    twoFactorApi.setupFromToken(setupToken)
      .then(res => {
        setQrCode(res.data.qr_code)
        setSecret(res.data.secret)
        setBackupCodes(res.data.backup_codes || [])
        setLoadState('ready')
      })
      .catch(() => setLoadState('error'))
  }, [setupToken])

  const copySecret = () => {
    navigator.clipboard.writeText(secret).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleEnable = async (e) => {
    e.preventDefault()
    if (otpValue.length < 6) return
    setSubmitting(true)
    setSubmitError('')
    try {
      await twoFactorApi.enableFromToken(setupToken, otpValue)
      setLoadState('done')
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Invalid code. Please try again.')
      setOtpValue('')
    } finally {
      setSubmitting(false)
    }
  }

  const bgStyle = { backgroundImage: 'linear-gradient(to bottom, #e8f1f8, #d0e3f0)' }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loadState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={bgStyle}>
        <div className="bg-white rounded-2xl shadow-xl p-10 flex flex-col items-center gap-4">
          <Loader className="w-8 h-8 animate-spin text-[#358ECD]" />
          <p className="text-gray-600">Loading your 2FA setup…</p>
        </div>
      </div>
    )
  }

  // ── Invalid / expired token ────────────────────────────────────────────────
  if (loadState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Setup Link Expired</h2>
          <p className="text-gray-500 mb-6 text-sm">
            This link has expired or is invalid. You can enable Google Authenticator
            anytime from your Account Settings.
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-2.5 rounded-lg text-white font-bold text-sm"
            style={{ backgroundColor: '#358ECD' }}
          >
            Go to Sign In
          </Link>
        </div>
      </div>
    )
  }

  // ── Done ───────────────────────────────────────────────────────────────────
  if (loadState === 'done') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">2FA Enabled!</h2>
          <p className="text-gray-500 mb-4 text-sm">
            Google Authenticator has been successfully set up for your account.
          </p>

          {/* Backup codes */}
          {backupCodes.length > 0 && (
            <div className="mb-6 text-left">
              <p className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                ⚠️ Save these backup codes in a safe place. Each can only be used once.
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {backupCodes.map((code, i) => (
                  <code key={i} className="text-xs bg-gray-100 text-gray-700 font-mono px-3 py-1.5 rounded text-center">
                    {code}
                  </code>
                ))}
              </div>
            </div>
          )}

          <Link
            to="/login"
            className="inline-block w-full py-3 rounded-lg text-white font-bold text-sm"
            style={{ backgroundColor: '#358ECD' }}
          >
            Sign In Now
          </Link>
        </div>
      </div>
    )
  }

  // ── Ready: show QR code ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={bgStyle}>
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-3">
            <ShieldCheck className="w-7 h-7 text-[#358ECD]" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Set Up Google Authenticator</h2>
          <p className="text-sm text-gray-500 mt-1">Secure your WQData LIVE account with 2FA</p>
        </div>

        {/* Steps */}
        <div className="space-y-5 mb-6">
          {/* Step 1 */}
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#358ECD] text-white text-xs font-bold flex items-center justify-center">1</span>
            <div>
              <p className="text-sm font-semibold text-gray-700">Install Google Authenticator</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Download <strong>Google Authenticator</strong> from the App Store or Google Play.
              </p>
            </div>
          </div>

          {/* Step 2 — QR code */}
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#358ECD] text-white text-xs font-bold flex items-center justify-center">2</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-700">Scan this QR code</p>
              <div className="mt-2 flex justify-center">
                <img
                  src={qrCode}
                  alt="2FA QR code"
                  className="w-40 h-40 border border-gray-200 rounded-lg p-1"
                />
              </div>
              {/* Manual key */}
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1 text-center">Or enter the key manually:</p>
                <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
                  <code className="flex-1 text-xs font-mono text-gray-700 break-all select-all">
                    {showSecret ? secret : secret.replace(/./g, '•')}
                  </code>
                  <button onClick={() => setShowSecret(v => !v)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                    {showSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button onClick={copySecret} className="text-gray-400 hover:text-[#358ECD] flex-shrink-0" title="Copy">
                    {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Step 3 — verify */}
          <div className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#358ECD] text-white text-xs font-bold flex items-center justify-center">3</span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-700 mb-2">Enter the 6-digit code</p>
              <OtpInput value={otpValue} onChange={setOtpValue} disabled={submitting} />
              {submitError && (
                <p className="text-xs text-red-600 text-center mt-2">{submitError}</p>
              )}
            </div>
          </div>
        </div>

        {/* Enable button */}
        <form onSubmit={handleEnable}>
          <button
            type="submit"
            disabled={submitting || otpValue.length < 6}
            className="w-full py-3 rounded-lg text-white font-bold text-sm tracking-wide
                       transition disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#358ECD' }}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader className="w-4 h-4 animate-spin" /> Enabling…
              </span>
            ) : 'Enable 2FA'}
          </button>
        </form>

        <p className="mt-4 text-center text-xs text-gray-400">
          You can skip this and enable 2FA later from{' '}
          <Link to="/login" className="text-[#358ECD] hover:underline">Account Settings</Link>.
        </p>
      </div>
    </div>
  )
}
